import { useState, useEffect, useCallback, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { TOAST_CLOSE_BUTTONS } from "@/utils/toast";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonTitle,
  IonContent, IonButtons, IonButton, IonIcon,
  IonPage, IonSegment, IonSegmentButton, IonLabel,
  IonList, IonItem, IonActionSheet, IonToast, IonSpinner, IonRippleEffect,
  IonSkeletonText, IonCard,
} from "@ionic/react";
import {
  menuOutline, closeOutline, moonOutline, sunnyOutline,
  briefcaseOutline, calculatorOutline, caretDownOutline, documentTextOutline, leafOutline, readerOutline, scaleOutline, chevronBackOutline, settingsOutline, addOutline, notificationsOutline, folderOpenOutline,
  mailOutline, walletOutline, giftOutline, personOutline, logOutOutline, logInOutline, heartOutline, optionsOutline, swapHorizontalOutline, peopleOutline, cardOutline, receiptOutline,
  createOutline, layersOutline, arrowUpCircleOutline, speedometerOutline,
} from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import FreshPopover from "./FreshPopover";
import { useTopbarSearch } from "./TopbarSearch";
import FreshOverlay from "./FreshOverlay";
import { APP_SECONDARY_PATHS } from "../utils/sidebarPages";
import { deliverPendingDownload, deliverDownload, isMobileBrowser, openDownloadInNewTab } from "../utils/pendingDownload";
import { processRecurringRuns } from "../utils/recurringStubs";
import { fetchSavedAssets, invalidateSavedAssets, currentUserId, rememberAssetAccess, rememberedAssetAccess, rememberActiveBusiness, rememberedActiveBusiness, useSavedAssets } from "./SavedAssets";
import { applyBrandColor, clearBrandColor, readStoredBrandColor, storeBrandColor, forgetBrandColor, siteDefaultBrandColor, storeSiteDefaultBrandColor } from "../utils/brandColor";
import UpsellPaywall, { readPendingUpsell, clearPendingUpsell, UPSELL_TAKEN_KEY } from "./UpsellPaywall";
import BusinessSetupModal from "./BusinessSetupModal";
import BusinessSwitcherPopover from "./BusinessSwitcher";
import { userHeaders, isUserLoggedIn } from "../utils/userAuth";
import { paywallsSuppressed } from "../utils/paywallGate";
import { getVisitorId } from "../utils/trackPageView";
import { registerPush } from "../utils/nativePush";
import { installedAppVersion, compareVersions, minAppVersion, latestAppVersion } from "../utils/appVersion";
import { setToastTemplates, toastText } from "../utils/toastTemplates";
import { syncStatusBar } from "../utils/statusBar";
import { appThemeDark, watchDeviceTheme } from "../utils/appTheme";
import { emitAppLive, APP_LIVE_EVENT } from "../utils/appLive";
import { GENERATOR_GROUPS } from "../utils/generatorAvailability";
import MintSlipLogo from "../assests/mintslip-logo.png";
import {
  getNotifications, markAllRead, removeNotification, clearAllNotifications,
  getPendingToasts, markToastShown, pullNotificationsFromAccount, isSignNotification, claimNotifications,
  setNotificationsRead, removeNotifications,
} from "../utils/appNotifications";
import { generateAndDownloadPaystub } from "../utils/paystubGenerator";
import {
  useDisabledGenerators, TAX_GENERATOR_IDS, LEGAL_GENERATOR_IDS, BUSINESS_GENERATOR_IDS,
} from "../utils/generatorAvailability";
import { generateAndDownloadCanadianPaystub } from "../utils/canadianPaystubGenerator";
import { generateAndDownloadUkPayslip } from "../utils/ukPayslipGenerator";
import { generateAndDownloadOfferLetter } from "../utils/offerLetterGenerator";
import { generateAndDownloadResume } from "../utils/resumeGenerator";
import { generateAndDownloadW2 } from "../utils/w2Generator";
import { generateAndDownloadW9 } from "../utils/w9Generator";
import { generateAndDownload1099NEC } from "../utils/1099necGenerator";
import { generateAndDownload1099MISC } from "../utils/1099miscGenerator";
import { generateAndDownloadScheduleC } from "../utils/scheduleCGenerator";
import { generateAndDownloadBankStatement } from "../utils/bankStatementGenerator";
import { generateAndDownloadCeaseAndDesist } from "../utils/ceaseAndDesistGenerator";
import { generateAndDownloadLegalDocument } from "../utils/legalDocumentGenerator";
import { generateAndDownloadPowerOfAttorney } from "../utils/powerOfAttorneyGenerator";
import { generateAndDownloadCommercialLease } from "../utils/commercialLeaseGenerator";
import { generateAndDownloadUtilityBill } from "../utils/utilityBillGenerator";
import { generateAndDownloadVehicleBillOfSale } from "../utils/vehicleBillOfSaleGenerator";
import AppOfferLetter from "../pages/app/AppOfferLetter";
import AppResumeBuilder from "../pages/app/AppResumeBuilder";
import AppTaxFormModal from "../pages/app/AppTaxFormModal";
import { BUSINESS_FORM_CONFIGS } from "../pages/app/appBusinessFormConfigs";
import SupportChatWidget from "./SupportChatWidget";
import PromoBanner from "./PromoBanner";
import SidebarProfileCard from "./SidebarProfileCard";
import NotificationsDrawer from "./NotificationsDrawer";
import PaymentMethodsModal from "./PaymentMethodsModal";
import InvoicesModal from "./InvoicesModal";
import { getNotifPrefs, setNotifPref, prefOn } from "../utils/notificationPrefs";
import PreviewPager from "./PreviewPager";
import BannedScreen from "./BannedScreen";
import { buildNotificationPreviewPages } from "../utils/notificationPreview";
import { fetchArchivedFile } from "../utils/archivedFile";
import { fileToPreviewPages } from "./DocumentPreviewModal";
import { confirmAlert } from "../utils/confirmAlert";
import { t, useLanguage } from "../utils/i18n";
import { startLiveTranslation } from "../utils/liveTranslate";
import "../admin-theme.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const tabs = [
  { id: "paystub",          label: "Pay Stubs",      icon: documentTextOutline, path: "/app/paystubs" },
  { id: "canadian-paystub", label: "Canadian Stubs", icon: leafOutline,         path: "/app/canadian-paystub" },
  { id: "uk-payslip",       label: "UK Payslips",    icon: receiptOutline,      path: "/app/uk-payslips" },
  { id: "tax-forms",        label: "Tax Forms",      icon: calculatorOutline,   path: "/app/tax-forms" },
  { id: "legal-forms",      label: "Legal Forms",    icon: scaleOutline,        path: "/app/legal-forms" },
  { id: "business-forms",   label: "Business Forms", icon: briefcaseOutline,    path: "/app/business-forms" },
  { id: "resumes",          label: "Resumes",        icon: readerOutline,       path: "/app/resumes" },
];

// ── /app search (the admin topbar search, for visitors and users) ──────────
// Searches what a visitor can reach: their documents, every generator, the
// tutorial videos and the app's pages. Documents and tutorials are fetched
// once per couple of minutes while searching.
const APP_SEARCH_GROUPS = {
  document: { label: "My Documents" },
  generator: { label: "Generators" },
  tutorial: { label: "Tutorials" },
  page: { label: "Pages" },
};
const GENERATOR_ROUTES = {
  paystub: ["/app/paystubs", "Pay Stubs"], "canadian-paystub": ["/app/canadian-paystub", "Canadian Stubs"], "uk-payslip": ["/app/uk-payslips", "UK Payslips"],
  "ai-resume": ["/app/resumes", "Resumes"], "offer-letter": ["/app/business-forms", "Business Forms"],
  w2: ["/app/tax-forms", "Tax Forms"], w9: ["/app/tax-forms", "Tax Forms"], "1099-nec": ["/app/tax-forms", "Tax Forms"],
  "1099-misc": ["/app/tax-forms", "Tax Forms"], "schedule-c": ["/app/tax-forms", "Tax Forms"],
  "cease-and-desist": ["/app/legal-forms", "Legal Forms"], "power-of-attorney": ["/app/legal-forms", "Legal Forms"],
  "vehicle-bill-of-sale": ["/app/legal-forms", "Legal Forms"],
  "commercial-lease": ["/app/business-forms", "Business Forms"], "utility-bill": ["/app/business-forms", "Business Forms"],
  "bank-statement": ["/app/business-forms", "Business Forms"],
};
const DOC_TYPE_LABELS = {
  paystub: "Pay Stub", "canadian-paystub": "Canadian Pay Stub", "uk-payslip": "UK Payslip", w2: "W-2", w9: "W-9", "1099-nec": "1099-NEC", "1099-misc": "1099-MISC",
  "schedule-c": "Schedule C", "offer-letter": "Offer Letter", "commercial-lease": "Commercial Lease", "utility-bill": "Service Expense",
  "bank-statement": "Accounting Mockup", "cease-and-desist": "Cease and Desist", "power-of-attorney": "Power of Attorney",
  "vehicle-bill-of-sale": "Vehicle Bill of Sale", resume: "Resume", "ai-resume": "Resume",
  "legal-document": "Legal Document", "custom-pdf": "Signed Document",
};
let _appSearchCache = { at: 0, docs: [], tutorials: [] };
async function loadAppSearchData() {
  if (Date.now() - _appSearchCache.at < 120000) return _appSearchCache;
  const p = new URLSearchParams();
  const vid = getVisitorId();
  if (vid) p.set("visitorId", vid);
  try {
    const ids = JSON.parse(localStorage.getItem("ms:docIds") || "[]");
    if (Array.isArray(ids) && ids.length) p.set("ids", ids.slice(-100).join(","));
  } catch { /* ignore */ }
  const [docs, tuts] = await Promise.allSettled([
    fetch(`${BACKEND_URL}/api/my-documents?${p.toString()}`, { headers: userHeaders() }).then((r) => r.json()),
    fetch(`${BACKEND_URL}/api/tutorial-categories`).then((r) => r.json()),
  ]);
  _appSearchCache = {
    at: Date.now(),
    docs: docs.status === "fulfilled" && docs.value?.success ? (docs.value.documents || []) : [],
    tutorials: tuts.status === "fulfilled" && tuts.value?.success ? (tuts.value.categories || []) : [],
  };
  return _appSearchCache;
}
export function clearAppSearchCache() { _appSearchCache = { at: 0, docs: [], tutorials: [] }; }
// On the web My Documents belongs to signed-in accounts (a guest reaches it
// only through an emailed documents link); the native app keeps it for guests
const showMyDocs = () => Capacitor.isNativePlatform?.() || !!localStorage.getItem("userToken");
// The notifications bell and drawer likewise: web guests don't get them
const showNotifications = showMyDocs;

async function appSearch(q, { billingEnabled = true, brandingAccess = false, docsAccess = true }) {
  const needle = q.toLowerCase();
  const hit = (...parts) => parts.some((x) => String(x || "").toLowerCase().includes(needle));
  const { docs, tutorials } = await loadAppSearchData();
  const generators = (GENERATOR_GROUPS.find((g) => g.title === "Generators")?.items || [])
    .filter((g) => hit(g.label, g.id, GENERATOR_ROUTES[g.id]?.[1]))
    .map((g) => ({ type: "generator", title: g.label, subtitle: GENERATOR_ROUTES[g.id]?.[1] || "", route: GENERATOR_ROUTES[g.id]?.[0] || "/app" }));
  const documents = (docsAccess ? docs : [])
    .filter((d) => hit(d.fileName, DOC_TYPE_LABELS[d.documentType], d.documentType))
    .map((d) => ({ type: "document", title: d.fileName || "Document", subtitle: [DOC_TYPE_LABELS[d.documentType] || d.documentType, d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ""].filter(Boolean).join(" · "), route: `/app/my-documents?doc=${encodeURIComponent(d.id)}` }));
  const videos = tutorials.flatMap((c) => (c.videos || []).filter((v) => v.title && hit(v.title, v.description, c.name))
    .map((v) => ({ type: "tutorial", title: v.title, subtitle: c.name || "Tutorials", route: `/app/settings?tutorial=${encodeURIComponent(v.id || "")}` })));
  const pages = [
    ...(docsAccess ? [{ title: "My Documents", route: "/app/my-documents" }] : []),
    ...(billingEnabled ? [{ title: "Billing", route: "/app/billing" }] : []),
    ...(isUserLoggedIn() ? [{ title: "Referrals", route: "/app/referrals" }] : []),
    ...(brandingAccess ? [{ title: "Preferences", route: "/app/preferences" }, { title: "Branding", route: "/app/preferences?segment=branding" }, { title: "Business", route: "/app/business" }, { title: "Employees", route: "/app/employees" }] : []),
    { title: "Tutorials", route: "/app/settings?tutorial=open" },
    { title: "Settings", route: "/app/settings" },
    { title: "Terms of Service", route: "/app/terms" },
    { title: "Privacy Policy", route: "/app/privacy" },
  ].filter((pg) => hit(pg.title)).map((pg) => ({ type: "page", title: pg.title, subtitle: "", route: pg.route }));
  return [...documents.slice(0, 6), ...generators.slice(0, 6), ...videos.slice(0, 6), ...pages.slice(0, 6)];
}

// Persists sidebar state across route-driven remounts
let _appSidebarOpen = true;

// Which "Give us some love" link the visitor gets: the native app knows its
// platform; in a browser an iPhone/iPad gets the App Store link, Android the
// Play link, everything else the web review page.
export function devicePlatform() {
  try {
    const p = Capacitor.getPlatform();
    if (p === "ios" || p === "android") return p;
  } catch { /* not running under Capacitor */ }
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "web";
}

function formatRelativeTime(ts) {
  const diff = Date.now() - ts;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

// Fresh-mount action sheet — same reliability pattern as AdminDetailModal:
// a controlled-isOpen Ionic overlay left permanently mounted can desync its
// internal "presented" flag under the layout's polling re-renders, after which
// flipping isOpen no longer presents anything and the button appears dead.
// Mounting a brand-new element per open removes any stale controller state;
// `render` outlives `isOpen` briefly so the close animation still plays.
// Ionic gives action sheet buttons a ripple in md mode only; this sheet keeps
// the ios look, so each button gets its ripple on present. A button already
// wears ion-activatable and clips its contents, which is all a ripple needs.
function addSheetRipples(sheet) {
  if (!sheet || !sheet.querySelectorAll) return;
  sheet.querySelectorAll(".action-sheet-button").forEach((btn) => {
    if (!btn.querySelector("ion-ripple-effect")) btn.appendChild(document.createElement("ion-ripple-effect"));
  });
}
function FreshActionSheet({ isOpen, onClose, onDidPresent, ...rest }) {
  const [render, setRender] = useState(isOpen);
  useEffect(() => { if (isOpen) setRender(true); }, [isOpen]);
  if (!render) return null;
  return (
    <IonActionSheet
      isOpen={isOpen}
      onDidPresent={(e) => { addSheetRipples(e.target); onDidPresent?.(e); }}
      onDidDismiss={() => { onClose?.(); setRender(false); }}
      {...rest}
    />
  );
}

// Popovers use the shared FreshPopover (components/FreshPopover.js): the same
// fresh-mount pattern as FreshActionSheet, since a trigger-based popover left
// permanently mounted desyncs its "presented" state under this layout's
// polling re-renders, after which taps do nothing.

// Which bell notifications this device shows, per the drawer's Preferences:
// a document category can be switched off, and failures separately.
const inGroup = (g, id) => (g instanceof Set ? g.has(id) : Array.isArray(g) && g.includes(id));
const notifCategory = (type) => {
  if (!type) return null;
  if (type === "paystub") return "paystub";
  if (type === "canadian-paystub") return "canadian-paystub";
  if (type === "uk-payslip") return "uk-payslip";
  if (inGroup(TAX_GENERATOR_IDS, type)) return "tax";
  if (inGroup(LEGAL_GENERATOR_IDS, type)) return "legal";
  if (inGroup(BUSINESS_GENERATOR_IDS, type)) return "business";
  if (/resume/.test(type)) return "resume";
  return null;
};
// A notification status colour is an Ionic colour name (theme-adaptive) or a
// literal hex; the shipped default per status is applied at the call site.
const notifStatusColor = (c) => (typeof c === "string" && c.startsWith("#") ? c : `var(--ion-color-${c || "medium"})`);

const notifVisible = (n, prefs) => {
  const cat = notifCategory(n.type);
  if (cat && !prefOn(prefs, cat)) return false;
  if (n.status === "error" && !prefOn(prefs, "failed")) return false;
  return true;
};
const appNotifPrefGroups = () => [
  {
    key: "documents", title: t("Documents"),
    description: t("Notified when a document you generated is ready to download"),
    toggles: [
      { key: "paystub", label: t("Pay Stubs") },
      { key: "canadian-paystub", label: t("Canadian Stubs") },
      { key: "uk-payslip", label: t("UK Payslips") },
      { key: "tax", label: t("Tax Forms") },
      { key: "legal", label: t("Legal Forms") },
      { key: "business", label: t("Business Forms") },
      { key: "resume", label: t("Resumes") },
    ],
  },
  { key: "failed", title: t("Failures"), description: t("Notified when a document could not be generated") },
  { key: "toasts", title: t("Pop-ups"), description: t("Show a pop-up when a document is ready for download") },
];

// pageTabs: { value, items: [{ value, label }], onChange(value) } puts a
// page's own segment in the top bar as a second row under the tabs
// (Preferences: All / Branding), the same shape AdminLayout takes. With
// dropdownOnMobile the row becomes, on a phone, the current tab's label
// opening the same popover list the top bar's own nav uses.
export default function AppLayout({ children, fillHeight = false, pageTabs }) {
  const navigate  = useNavigate();
  const location  = useLocation();

  useLanguage(); // re-render the chrome when the language changes
  useEffect(() => startLiveTranslation(), []); // and everything else translates live
  // Native app: register this device for push notifications (no-op on web;
  // re-runs after sign-in so the device moves onto the account).
  useEffect(() => { registerPush(); }, []);
  // (Push tap navigation lives in App.js's PushTapNavigator, which stays
  // mounted on /admin pages too, where this layout does not.)
  const [isMobile,          setIsMobile]          = useState(window.innerWidth < 768);
  const [navMenu,           setNavMenu]           = useState({ open: false, event: undefined });
  const [pageTabMenu,       setPageTabMenu]       = useState({ open: false, event: undefined }); // a page's own tabs as a phone dropdown
  const [darkMode,          setDarkMode]           = useState(() => appThemeDark());

  // Post-purchase upsell: the success page leaves the offer in session
  // storage when it returns to the app; it is shown here in the paywall's
  // clothes. Picking a document records the discount for that document's
  // checkout and opens its generator; closing drops the offer.
  const [upsellOffer, setUpsellOffer] = useState(null);
  useEffect(() => {
    const pending = readPendingUpsell();
    if (pending && (paywallsSuppressed() || Capacitor.isNativePlatform?.())) { clearPendingUpsell(); return; }
    if (pending) setUpsellOffer(pending);
  }, [location.pathname]); // eslint-disable-line
  const dismissUpsell = () => { clearPendingUpsell(); setUpsellOffer(null); };
  const pickUpsell = (target) => {
    try {
      sessionStorage.setItem(UPSELL_TAKEN_KEY, JSON.stringify({
        expiresAt: upsellOffer.expiresAt, percent: upsellOffer.discountPercent,
        targets: upsellOffer.targets.map((t) => t.documentType),
      }));
    } catch {}
    clearPendingUpsell();
    setUpsellOffer(null);
    navigate(target.path);
  };
  // Topbar search — the same control as the admin dashboard
  const appSearchUid = (() => { try { return JSON.parse(localStorage.getItem("userInfo") || "{}").id || getVisitorId() || null; } catch { return getVisitorId() || null; } })();
  const search = useTopbarSearch({
    groups: APP_SEARCH_GROUPS,
    recentsUid: appSearchUid,
    recentsPrefix: "mintslip:appRecent",
    placeholder: t("Search"),
    dark: darkMode,
    search: (q) => appSearch(q, { billingEnabled: showBilling, brandingAccess, docsAccess: showMyDocs() }),
    onOpen: (r) => navigate(r.route),
  });
  const [sidebarOpen,       _setSidebarOpen]        = useState(_appSidebarOpen);
  const setSidebarOpen = (valOrFn) => {
    _setSidebarOpen(prev => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      _appSidebarOpen = next;
      return next;
    });
  };
  const [mobileSidebarOpen, setMobileSidebarOpen]  = useState(false);
  const [pmModalOpen, setPmModalOpen] = useState(false);   // Payment methods, from the user popover
  const [invModalOpen, setInvModalOpen] = useState(false); // Invoices, from the user popover
  // The user-card popover (Manage profile / Log out, or Log in as a guest)
  const [userMenu,          setUserMenu]           = useState({ open: false, event: undefined });
  const [authTick, setAuthTick] = useState(0); // bumps a re-render after sign-in/out (userInfo is read from storage)
  // The account page saves a new name / photo to storage and fires this so the
  // sidebar card and the top-bar title follow at once
  useEffect(() => {
    const onUser = () => setAuthTick((n) => n + 1);
    window.addEventListener("mintslip-user-updated", onUser);
    return () => window.removeEventListener("mintslip-user-updated", onUser);
  }, []);
  // The desktop landing always arrives with the sidebar open no matter the
  // persisted toggle; its menu and close buttons still collapse it for the
  // visit. Tracked separately so a change here never flips the remembered
  // state for the generator pages.
  const [landingSidebarOpen, setLandingSidebarOpen] = useState(true);
  useEffect(() => { setLandingSidebarOpen(true); }, [location.pathname]);
  const [createOpen,        setCreateOpen]          = useState(false);
  const [offerLetterOpen,   setOfferLetterOpen]     = useState(false);
  const [accountingOpen,    setAccountingOpen]      = useState(false);
  const [resumeBuilderOpen, setResumeBuilderOpen]   = useState(false);

  // Notifications state
  const [notifications,     setNotifications]       = useState(() => getNotifications());
  const [notifDrawerOpen,   setNotifDrawerOpen]     = useState(false);
  const [notifPrefs,        setNotifPrefs]          = useState(() => getNotifPrefs("app"));
  const [toastOpen,         setToastOpen]           = useState(false);
  const [toastMessage,      setToastMessage]        = useState("");
  const [toastColor,        setToastColor]          = useState("success"); // danger for a failure
  const [lastDownload,      setLastDownload]        = useState(null); // the file just delivered after checkout
  const [redownloadingId,   setRedownloadingId]     = useState(null);
  // Notification preview modal — shows the pages of the document the user
  // bought, swipeable + slider via <PreviewPager>.
  const [previewNotif,      setPreviewNotif]        = useState(null);
  const [previewPages,      setPreviewPages]        = useState([]);
  const [previewIndex,      setPreviewIndex]        = useState(0);
  const [previewLoading,    setPreviewLoading]      = useState(false);

  const openPreview = useCallback(async (notif) => {
    // A signature-request event opens its signing page, not the preview
    // modal. Newer notifications carry the link; older ones only embed the
    // request id in their notification id (sign_<requestId>_<timestamp>),
    // so the link is looked up from the sender's own request list.
    if (isSignNotification(notif) || String(notif.id || "").startsWith("sign_")) {
      setNotificationsRead([notif.id], true);
      setNotifications(getNotifications());
      setNotifDrawerOpen(false);
      let token = notif.signToken;
      if (!token) {
        const m = String(notif.id || "").match(/^sign_(.+)_\d+$/);
        if (m) {
          try {
            const res = await fetch(`${BACKEND_URL}/api/user/signature-requests`, { headers: userHeaders() });
            const d = await res.json().catch(() => ({}));
            const row = (d.requests || []).find((r) => r.id === m[1]);
            token = row ? String(row.link || "").split("/sign/")[1] : "";
          } catch { /* offline: fall through to the toast */ }
        }
      }
      // a new comment opens the signing page with its comments open
      if (token) { navigate(`/sign/${encodeURIComponent(token)}${notif.titleKey === "commented" ? "?comments=1" : ""}`); return; }
      setToastColor("danger");
      setToastMessage("That signature request is no longer available");
      setToastOpen(true);
      return;
    }
    // The preview modal is for actual documents only. Informational bells
    // carry no file: a gift of downloads opens the account page at the
    // downloads area; anything else just marks itself read.
    if (!notif.fileType && !notif.docId) {
      setNotificationsRead([notif.id], true);
      setNotifications(getNotifications());
      if (notif.type === "gift" || String(notif.id || "").startsWith("gift_")) {
        setNotifDrawerOpen(false);
        navigate("/app/account#downloads");
      }
      // a referral notice: the Referrals page; the new account's discount notice, the plans
      if (notif.type === "referral") {
        setNotifDrawerOpen(false);
        navigate(notif.titleKey === "referral_discount" ? "/app/billing" : "/app/referrals");
      }
      return;
    }
    setPreviewNotif(notif);
    setPreviewPages([]);
    setPreviewIndex(0);
    if (notif.status !== "ready") return;
    setPreviewLoading(true);
    try {
      // The preview generators are CPU-bound and largely synchronous (jsPDF),
      // so yield a beat first — otherwise React flushes only the final state
      // and the skeleton never paints.
      await new Promise((r) => setTimeout(r, 50));
      let pages = await buildNotificationPreviewPages(notif);
      // No form data on this device (another device, or already cleared) —
      // render the archived copy instead when the notification points at one.
      if (!pages.length && notif.docId) {
        try { pages = await fileToPreviewPages(await fetchArchivedFile(notif.docId), notif.fileName); } catch { pages = []; }
      }
      // Ignore if the modal was closed or switched while we were rendering.
      setPreviewNotif((cur) => {
        if (cur && cur.id === notif.id) setPreviewPages(pages);
        return cur;
      });
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    dismissModal(() => {
      setPreviewNotif(null);
      setPreviewPages([]);
      setPreviewIndex(0);
      setPreviewLoading(false);
    });
  }, []);

  const refreshNotifications = useCallback(() => {
    setNotifications(getNotifications());
  }, []);

  // Toast anything newly arrived (a ready download, a comment, a signature).
  // Held in a ref so the mount effect and the 30s account poll both call the
  // latest closure without retriggering their effects.
  const showPendingToastsRef = useRef(() => {});
  // the page scroller: a page-tab switch starts the new segment at the top
  // (the page stays mounted, so the old segment's scroll would carry over)
  const mainContentRef = useRef(null);
  const changePageTab = (v) => {
    if (!pageTabs || v === pageTabs.value) return;
    pageTabs.onChange?.(v);
    try { mainContentRef.current?.scrollToTop?.(0); } catch { /* no scroller yet */ }
  };
  showPendingToastsRef.current = () => {
    const pending = prefOn(notifPrefs, "toasts") ? getPendingToasts().filter((n) => notifVisible(n, notifPrefs)) : [];
    if (pending.length === 0) return;
    const first = pending[0];
    setToastMessage(isSignNotification(first) || first.titleKey === "downloads_gifted" || first.type === "referral"
      ? toastText("signature_update", { title: resolveNotifTitle(first) }, "{title}")
      : toastText("download_ready", { file: first.fileName || "Your file" }, "{file} is ready for download"));
    setToastOpen(true);
    pending.forEach((n) => markToastShown(n.id));
    refreshNotifications();
  };

  // Signed in: the drawer follows the account — merge the server copy on mount
  useEffect(() => {
    let token = "";
    try { token = localStorage.getItem("userToken") || ""; } catch {}
    if (!token) return;
    claimNotifications(currentUserId()); // another account's local bell never leaks in
    pullNotificationsFromAccount().then((merged) => { if (merged) { refreshNotifications(); showPendingToastsRef.current(); } });
  }, [refreshNotifications]);

  // Native: coming back from the website (download packs, a new plan bought
  // in the browser) refreshes the account so the open page sees the new
  // entitlements without a restart.
  useEffect(() => {
    if (!Capacitor.isNativePlatform?.()) return undefined;
    let remove = null;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const sub = await CapApp.addListener("appStateChange", async ({ isActive }) => {
          if (!isActive) return;
          let token = "";
          try { token = localStorage.getItem("userToken") || ""; } catch {}
          if (!token) return;
          try {
            const res = await fetch(`${BACKEND_URL}/api/user/me`, { headers: { Authorization: `Bearer ${token}` } });
            const d = await res.json();
            if (d.success && d.user) {
              localStorage.setItem("userInfo", JSON.stringify(d.user));
              window.dispatchEvent(new Event("ms:entitlements-changed"));
            }
          } catch { /* offline resume: nothing to refresh */ }
        });
        remove = () => sub.remove();
      } catch { /* plugin unavailable */ }
    })();
    return () => { try { remove?.(); } catch { /* already gone */ } };
  }, []);

  // Live (the admin's pattern): while signed in, poll the account every 30s.
  // Anything new refreshes the bell and tells the page on screen to re-fetch
  // its data (My Documents...), see utils/appLive.
  useEffect(() => {
    let token = "";
    try { token = localStorage.getItem("userToken") || ""; } catch {}
    if (!token) return;
    let stopped = false;
    const tick = async () => {
      if (document.visibilityState === "hidden") return;
      // due recurring stubs generate while the app is open (self-throttled)
      processRecurringRuns();
      const before = new Set(getNotifications().map((n) => n.id));
      const merged = await pullNotificationsFromAccount();
      if (stopped || !merged) return;
      if (getNotifications().some((n) => !before.has(n.id))) {
        refreshNotifications();
        clearAppSearchCache();
        emitAppLive({ reason: "notifications" });
        showPendingToastsRef.current();
      }
    };
    const iv = setInterval(tick, 30000);
    tick();
    return () => { stopped = true; clearInterval(iv); };
  }, [refreshNotifications]);

  // Banned visitors can still browse the public site, but not /app. The ban
  // screen renders as a non-dismissible OVERLAY on top of the live app (the
  // paywall pattern) so its scrim backdrop-blurs the real /app behind it.
  const [isBanned, setIsBanned] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/check-ip-ban`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.banned) setIsBanned(true); })
      .catch(() => {});
  }, []);

  // On mount, show toast for any newly ready notifications
  useEffect(() => {
    showPendingToastsRef.current();
    // Returning from the in-app checkout: the payment-success screen sets
    // this flag so the bell picks up the fresh download. The drawer itself
    // stays closed (the ready toast announces the file); the user opens it.
    try {
      if (localStorage.getItem("appOpenNotifDrawer") === "1") {
        localStorage.removeItem("appOpenNotifDrawer");
        refreshNotifications();
        // The new document is archived: My Documents (and search) pick it up
        clearAppSearchCache();
        emitAppLive({ reason: "download" });
        // The paid document waits for the app to be on screen before it
        // downloads (a new tab on phone browsers, in place elsewhere)
        setTimeout(() => { const d = deliverPendingDownload(); if (d) setLastDownload(d); }, 600);
      }
    } catch {}
  }, []); // eslint-disable-line

  // A Pro or Enterprise subscriber's own brand colour tints the top bar and
  // the buttons in that colour. The cached value paints immediately so the bar
  // does not flash green, then the gated /api/user/assets response is the
  // authority: no colour in it (a lapsed plan, or none set) clears both the
  // override and the cache. Cleared on unmount so the admin and the marketing
  // pages keep the theme's green.
  const [brandingAccess, setBrandingAccess] = useState(() => isUserLoggedIn() && rememberedAssetAccess(currentUserId()));
  useEffect(() => {
    let live = true;
    const uid = currentUserId();
    applyBrandColor(readStoredBrandColor("app", uid) || siteDefaultBrandColor());
    const reconcile = async () => {
      const d = await fetchSavedAssets();
      if (!live || currentUserId() !== uid) return;
      // Branding belongs to the Pro and Enterprise plans, so the sidebar link
      // is not offered to anyone else at all.
      setBrandingAccess(!!d.hasAccess);
      rememberAssetAccess(uid, !!d.hasAccess);
      const next = (d.hasAccess && d.primaryColor) || null;
      if (next) { storeBrandColor(next, "app", uid); applyBrandColor(next); }
      else {
        // no colour of their own: the admin's site colour is the default
        forgetBrandColor("app", uid);
        const site = siteDefaultBrandColor();
        if (site) applyBrandColor(site); else clearBrandColor();
      }
    };
    reconcile();
    window.addEventListener(APP_LIVE_EVENT, reconcile);
    return () => { live = false; window.removeEventListener(APP_LIVE_EVENT, reconcile); clearBrandColor(); };
  }, [authTick]); // eslint-disable-line

  const handleMenuToggle = () => {
    if (window.innerWidth < 768) {
      setMobileSidebarOpen(v => !v);
    } else if (location.pathname === "/app" || location.pathname === "/app/") {
      setLandingSidebarOpen(v => !v);
    } else {
      setSidebarOpen(prev => !prev);
    }
  };

  const handleCloseSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileSidebarOpen(false);
    } else if (location.pathname === "/app" || location.pathname === "/app/") {
      setLandingSidebarOpen(false);
    } else {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
    syncStatusBar(); // phone status bar icons follow the theme
  }, [darkMode]);
  // The sun/moon is the explicit choice that sticks; a device-derived default
  // is never persisted, so the phone's own setting keeps steering until then
  const toggleDark = () => setDarkMode((prev) => {
    const next = !prev;
    try { localStorage.setItem("appDarkMode", String(next)); } catch { /* theme still flips */ }
    return next;
  });
  useEffect(() => watchDeviceTheme(setDarkMode), []);

  // the branding preview's sun/moon flips the app theme; follow it here
  useEffect(() => {
    const onTheme = (e) => setDarkMode(!!e.detail?.dark);
    window.addEventListener("mintslip-theme-change", onTheme);
    return () => window.removeEventListener("mintslip-theme-change", onTheme);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("ionic-active");
    document.body.classList.add("ionic-active");
    return () => {
      document.documentElement.classList.remove("ionic-active");
      document.body.classList.remove("ionic-active");
    };
  }, []);


  // Every /app page mounts its own AppLayout, so these categories are seeded
  // synchronously from the last-known set (cached) and only then refreshed in
  // the background — otherwise a custom tab would blink out and shift the
  // topbar on every navigation.
  const readCatCache = (key) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };
  const writeCatCache = (key, cats) => { try { localStorage.setItem(key, JSON.stringify(cats || [])); } catch { /* private mode */ } };
  // Scoped to the exact account + active business the data is scoped to, so a
  // cached set can never paint for another user (or another of the account's
  // businesses) on a shared device.
  const myCatsKey = `ms:myCats:${currentUserId() || "guest"}:${(rememberedActiveBusiness(currentUserId()) || {}).id || "own"}`;

  // Admin-created custom categories: each becomes its own topbar tab once a
  // template is published under it (the backend only lists live ones).
  const [customCategories, setCustomCategories] = useState(() => readCatCache("ms:customCats"));
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/doc-template-categories`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.success) { setCustomCategories(d.categories || []); writeCatCache("ms:customCats", d.categories || []); } })
      .catch(() => {});
  }, []);
  // the business's OWN published categories — its private topbar tabs + Create items
  const [myCategories, setMyCategories] = useState(() => readCatCache(`ms:myCats:${currentUserId() || "guest"}:${(rememberedActiveBusiness(currentUserId()) || {}).id || "own"}`));
  useEffect(() => {
    if (!isUserLoggedIn()) { setMyCategories([]); return undefined; }
    const load = () => fetch(`${BACKEND_URL}/api/user/doc-template-categories?live=1`, { headers: userHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.success && d.hasAccess) { setMyCategories(d.categories || []); writeCatCache(myCatsKey, d.categories || []); } })
      .catch(() => {});
    load();
    window.addEventListener("ms:create-menu-changed", load); // Templates -> Categories saved
    return () => window.removeEventListener("ms:create-menu-changed", load);
  }, [authTick]); // eslint-disable-line

  // the business's Create menu: the order of its items and the built-in
  // entries it hides (Templates -> Categories), cached like the categories
  const menuKey = `ms:createMenu:${currentUserId() || "guest"}:${(rememberedActiveBusiness(currentUserId()) || {}).id || "own"}`;
  const readMenuCache = () => { try { return JSON.parse(localStorage.getItem(menuKey) || "null") || { order: [], hidden: [] }; } catch { return { order: [], hidden: [] }; } };
  const [createMenu, setCreateMenu] = useState(readMenuCache);
  useEffect(() => {
    if (!isUserLoggedIn()) { setCreateMenu({ order: [], hidden: [] }); return undefined; }
    const load = () => fetch(`${BACKEND_URL}/api/user/create-menu`, { headers: userHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.success || !d.hasAccess) return;
        const m = { order: d.order || [], hidden: d.hidden || [] };
        setCreateMenu(m);
        try { localStorage.setItem(menuKey, JSON.stringify(m)); } catch { /* private mode */ }
      })
      .catch(() => {});
    load();
    window.addEventListener("ms:create-menu-changed", load);
    return () => window.removeEventListener("ms:create-menu-changed", load);
  }, [authTick]); // eslint-disable-line

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes("/app/canadian-paystub")) return "canadian-paystub";
    if (path.includes("/app/uk-payslips"))      return "uk-payslip";
    if (path.includes("/app/tax-forms"))        return "tax-forms";
    if (path.includes("/app/legal-forms"))      return "legal-forms";
    if (path.includes("/app/business-forms"))   return "business-forms";
    if (path.includes("/app/resumes"))          return "resumes";
    if (path.includes("/app/paystub"))          return "paystub";
    const custom = path.match(/\/app\/c\/([\w-]+)/);
    if (custom) return custom[1];
    return "home"; // the /app landing highlights no tab
  };

  const activeTab = getActiveTab();

  // Server-driven availability: tabs/actions vanish as soon as the admin
  // disables a generator (fetched at runtime — no app-store update needed).
  const disabledGenerators = useDisabledGenerators();
  const groupAllDisabled = (ids) => ids.every((id) => disabledGenerators.has(id));
  const tabHidden = {
    "paystub":          disabledGenerators.has("paystub"),
    "canadian-paystub": disabledGenerators.has("canadian-paystub"),
    "uk-payslip":       disabledGenerators.has("uk-payslip"),
    "tax-forms":        groupAllDisabled(TAX_GENERATOR_IDS),
    "legal-forms":      groupAllDisabled(LEGAL_GENERATOR_IDS),
    "business-forms":   groupAllDisabled(BUSINESS_GENERATOR_IDS),
    "resumes":          disabledGenerators.has("ai-resume"),
  };
  const allCustomCategories = [
    ...customCategories,
    ...myCategories.filter((mc) => !customCategories.some((c) => c.slug === mc.slug)),
  ];
  // the topbar shows every published category; the Create action sheet honours
  // each of the user's own categories' show/hide-in-create toggle.
  const actionSheetCategories = allCustomCategories.filter((c) => c.inActionSheet !== false);
  const visibleTabs = [
    ...tabs.filter((tab) => !tabHidden[tab.id]),
    ...allCustomCategories.map((c) => ({
      id: c.slug, label: c.label, icon: documentTextOutline, path: `/app/c/${c.slug}`,
    })),
  ];

  const isSecondaryPage = APP_SECONDARY_PATHS.includes(location.pathname) || location.pathname.startsWith("/app/templates/edit") || location.pathname.startsWith("/app/signatures/documents/");
  const isLanding = location.pathname === "/app" || location.pathname === "/app/";
  const { business, businesses, activeBusinessId, hasAccess: assetsAccess } = useSavedAssets();

  // The account page is titled with the person's name, like a user's page in the admin
  const storedName = (() => { try { return (JSON.parse(localStorage.getItem("userInfo") || "null") || {}).name || ""; } catch { return ""; } })();
  const pageTitle = location.pathname === "/app/account"
    ? (storedName || t("Account"))
    : location.pathname === "/app/my-documents" && assetsAccess && business?.name
    ? `${business.name} ${t("Documents")}`
    : t({
      "/app/terms":        "Terms of Service",
      "/app/privacy":      "Privacy Policy",
      "/app/settings":     "Settings",
      "/app/my-documents": "My Documents",
      "/app/preferences":  "Preferences",
      "/app/business":     "Business",
      "/app/employees":    "Employees",
      "/app/signatures":   "Signatures",
      "/app/templates":    "Templates",
      "/app/branding":     "Branding",
      "/app/billing":      "Billing",
      "/app/referrals":    "Referrals",
    }[location.pathname] || (location.pathname.startsWith("/app/templates/edit") ? t("Edit Template")
      : location.pathname.startsWith("/app/signatures/documents/") ? t("Edit Document") : ""));

  // Sidebar links — the same treatment as the admin sidebar's nav buttons.
  // "My Documents" sits directly under the profile card; Settings / Terms /
  // Privacy stay pinned to the bottom.
  // "Give us some love" — the admin's review link for this visitor's device
  // (App Store / Play Store / web); hidden until one is configured.
  const [loveLinks, setLoveLinks] = useState(null);
  // "Billing" sits under My Documents; the admin can hide it (Subscriptions page).
  // The native app sells nothing itself (store rules): no Billing link there;
  // its Billing page only links out to the website.
  const [billingEnabled, setBillingEnabled] = useState(true);
  const showBilling = billingEnabled && !Capacitor.isNativePlatform?.();
  const [notifStatuses, setNotifStatuses] = useState({}); // admin-customized status label/colour
  const [notifTitles, setNotifTitles] = useState({});     // admin-customized title templates
  const [notifTitleFields, setNotifTitleFields] = useState([]); // registry (defaults + tokens)
  const [minVersion, setMinVersion] = useState(""); // native apps below this must update
  const [updateMeta, setUpdateMeta] = useState({ latest: "", whatsNew: [] }); // for the force-update sheet
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/app-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.success) { setLoveLinks(d.settings?.loveLinks || null); setMinVersion(minAppVersion(d.settings, devicePlatform())); setUpdateMeta({ latest: latestAppVersion(d.settings, devicePlatform()), whatsNew: Array.isArray(d.settings?.whatsNew) ? d.settings.whatsNew.filter(Boolean) : [] }); setBillingEnabled(d.settings?.billingLink !== false); setNotifStatuses(d.settings?.notificationStatuses || {}); setNotifTitles(d.settings?.notificationTitles || {}); setNotifTitleFields(d.settings?.notificationTitleFields || []); setToastTemplates(d.settings?.toastMessages || {});
        // the admin's site colour is /app's default: wear it now unless this
        // user carries their own
        storeSiteDefaultBrandColor(d.settings?.brandColor);
        if (!readStoredBrandColor("app", currentUserId())) { const s = siteDefaultBrandColor(); if (s) applyBrandColor(s); }
      } })
      .catch(() => {});
  }, []);

  // The force-update wall: a native app older than the admin's minimum
  // supported version is blocked until it updates (the web is never blocked,
  // it is always the latest deploy).
  const [forceUpdate, setForceUpdate] = useState(false);
  useEffect(() => {
    if (!minVersion) { setForceUpdate(false); return undefined; }
    let on = true;
    installedAppVersion().then((v) => { if (on) setForceUpdate(!!v && compareVersions(v, minVersion) < 0); });
    return () => { on = false; };
  }, [minVersion]);
  const loveUrl = (loveLinks && loveLinks[devicePlatform()]) || "";
  const topLinks = [
    ...(showMyDocs() ? [{ label: "My Documents", icon: folderOpenOutline, path: "/app/my-documents" }] : []),
    // Preferences currently holds Branding, which is a Pro and Enterprise
    // feature, so the link is not shown on plans that do not include it
    ...(brandingAccess ? [{ label: "Employees", icon: peopleOutline, path: "/app/employees" }] : []),
    ...(brandingAccess ? [{ label: "Signatures", icon: createOutline, path: "/app/signatures" }] : []),
    ...(brandingAccess ? [{ label: "Templates", icon: layersOutline, path: "/app/templates" }] : []),
    ...(brandingAccess ? [{ label: "Preferences", icon: optionsOutline, path: "/app/preferences" }] : []),
    ...(showBilling ? [{ label: "Billing", icon: walletOutline, path: "/app/billing" }] : []),
    // the referral program is an account's own: signed-in accounts only
    ...(isUserLoggedIn() ? [{ label: "Referrals", icon: giftOutline, path: "/app/referrals" }] : []),
    ...(loveUrl ? [{ label: "Give us some love", icon: heartOutline, href: loveUrl }] : []),
  ];
  const openLink = ({ path, href }) => {
    if (href) window.open(href, "_blank", "noopener");
    else navigate(path);
  };
  const bottomLinks = [
    { label: "Settings",         icon: settingsOutline,     path: "/app/settings" },
  ];
  const sidebarLinkStyle = {
    width: "100%",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "0 20px",
    minHeight: 48,
    textAlign: "left",
    color: "var(--ion-text-color)",
    fontFamily: "var(--ion-font-family, system-ui)",
    fontSize: "0.9375rem",
    position: "relative",
    overflow: "hidden",
  };

  // Read user info from localStorage
  const userInfo = (() => {
    try { return JSON.parse(localStorage.getItem("userInfo")); }
    catch { return null; }
  })();

  // The sidebar profile row opens the user popover (Manage profile / Log out
  // when signed in, Log in as a guest), the same way the admin card does.
  const signedIn = !!localStorage.getItem("userToken");
  // A Pro/Enterprise account's own business rides on the same gated response
  // as the saved assets; the row renders once it is known, like the admin's.
  const [bizMenu, setBizMenu] = useState({ open: false, event: undefined });
  const [bizSwitcher, setBizSwitcher] = useState({ open: false, event: undefined });
  const [bizSetupOpen, setBizSetupOpen] = useState(false); // false | "setup" | "create"
  const businessRow = () => ({
    kicker: t("Business"),
    name: business?.name || t("Add your business"),
    src: business?.logo || undefined,
    // Until a business is saved, the row IS the setup: plans activated with no
    // client-side moment (admin-assigned ones) still get the sheet from here.
    onClick: (e) => (business?.name ? setBizMenu({ open: true, event: e.nativeEvent }) : setBizSetupOpen("setup")),
  });
  // An admin signed in through /login also carries the admin session: the
  // sidebar then shows MintSlip itself as a business card (site branding)
  // whose menu links into the admin area.
  const adminSide = signedIn && !!localStorage.getItem("adminToken");
  const [adminMenu, setAdminMenu] = useState({ open: false, event: undefined });
  const [siteBrand, setSiteBrand] = useState(null);
  useEffect(() => {
    if (!adminSide) return;
    fetch(`${BACKEND_URL}/api/admin/site-branding`, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.success) setSiteBrand(d); })
      .catch(() => {});
  }, [adminSide]);
  const mintslipRow = () => ({
    kicker: t("Business"),
    name: (siteBrand && siteBrand.name) || "MintSlip",
    src: (siteBrand && siteBrand.logo) || undefined,
    onClick: (e) => setAdminMenu({ open: true, event: e.nativeEvent }),
  });
  const profileRow = () => ({
    kicker: t("User"),
    name: userInfo?.name || userInfo?.email || t("Guest"),
    src: userInfo?.photo,
    onClick: (e) => setUserMenu({ open: true, event: e.nativeEvent }),
  });
  const closeUserMenu = () => setUserMenu({ open: false, event: undefined });
  const handleUserLogout = () => {
    // Signing out here does not unmount the layout, so the colour, its cached
    // value and the shared assets cache are dropped by hand: the next person
    // on this browser must not get the last one's brand.
    const uid = currentUserId();
    localStorage.removeItem("userToken");
    localStorage.removeItem("userInfo");
    // One sign-in opened both sides, so one sign-out closes both
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminInfo");
    localStorage.removeItem("adminRole");
    localStorage.removeItem("adminPermissions");
    forgetBrandColor("app", uid);
    clearBrandColor();
    rememberAssetAccess(uid, false);
    rememberActiveBusiness(uid, null);
    setBrandingAccess(false);
    invalidateSavedAssets();
    setAuthTick((n) => n + 1);
    navigate("/app");
  };
  // Pages can sign the user out through the layout's own cleanup (the
  // billing page's no-plan card does) with one window event.
  useEffect(() => {
    const onLogout = () => handleUserLogout();
    window.addEventListener("app-logout", onLogout);
    return () => window.removeEventListener("app-logout", onLogout);
  });

  // A Pro/Enterprise drawer follows the business switcher: a notification
  // made while operating one business shows only there. Untagged ones (from
  // before businesses, or made signed out) live under the primary business.
  // Express and guests see everything, exactly as before.
  const activeBizRow = businesses.find((b) => b.id === activeBusinessId);
  const bizNotifVisible = (n) => {
    if (!assetsAccess || businesses.length === 0) return true;
    if (!n.businessId) return !activeBizRow || !!activeBizRow.primary;
    return n.businessId === activeBusinessId;
  };
  // Only the 10 most recent are shown in the drawer (newest first).
  const resolveNotifTitle = (n) => {
    if (!n || !n.titleKey) return (n && n.fileName) || "";
    const field = (notifTitleFields || []).find((f) => f.key === n.titleKey);
    const template = (notifTitles || {})[n.titleKey] || (field && field.default) || n.fileName || "";
    const tokens = n.titleTokens || {};
    const filled = String(template).replace(/\{(\w+)\}/g, (_, k) => (tokens[k] != null ? tokens[k] : "")).trim();
    return filled || n.fileName || "";
  };
  const visibleNotifications = notifications.filter((n) => notifVisible(n, notifPrefs) && bizNotifVisible(n)).slice(0, 10);
  const unreadCount = visibleNotifications.filter((n) => !n.read).length;

  const openNotifDrawer = () => {
    setNotifDrawerOpen(true);
    markAllRead();
    refreshNotifications();
  };

  const closeNotifDrawer = () => {
    setNotifDrawerOpen(false);
    refreshNotifications();
  };

  const handleRedownload = async (notif) => {
    setRedownloadingId(notif.id);
    try {
      // Archived copy first — it works on any device (signed in, or this
      // browser's own archives); fall back to regenerating from local data.
      if (notif.docId) {
        try {
          deliverDownload(await fetchArchivedFile(notif.docId), notif.fileName || "document");
          return;
        } catch (err) {
          console.warn("Archived copy unavailable, regenerating locally:", err?.message || err);
        }
      }
      if (notif.type === "paystub") {
        const str = localStorage.getItem("pendingPaystubData");
        if (!str) throw new Error("no data");
        const data = JSON.parse(str);
        const template = localStorage.getItem("pendingPaystubTemplate") || "template-a";
        const count = parseInt(localStorage.getItem("pendingPaystubCount") || "1", 10);
        await generateAndDownloadPaystub(data, template, count);
      } else if (notif.type === "canadian-paystub") {
        const str = localStorage.getItem("pendingCanadianPaystubData");
        if (!str) throw new Error("no data");
        const data = JSON.parse(str);
        const template = localStorage.getItem("pendingCanadianPaystubTemplate") || "template-a";
        const count = parseInt(localStorage.getItem("pendingCanadianPaystubCount") || "1", 10);
        await generateAndDownloadCanadianPaystub(data, template, count);
      } else if (notif.type === "uk-payslip") {
        const str = localStorage.getItem("pendingUkPayslipData");
        if (!str) throw new Error("no data");
        const data = JSON.parse(str);
        const template = localStorage.getItem("pendingUkPayslipTemplate") || "template-h";
        const count = parseInt(localStorage.getItem("pendingUkPayslipCount") || "1", 10);
        await generateAndDownloadUkPayslip(data, template, count);
      } else if (notif.type === "offer-letter") {
        const str = localStorage.getItem("pendingOfferLetterData");
        if (!str) throw new Error("no data");
        const data = JSON.parse(str);
        await generateAndDownloadOfferLetter(data, false);
      } else if (notif.type === "ai-resume" || notif.type === "resume") {
        const str = localStorage.getItem("pendingResumeData");
        if (!str) throw new Error("no data");
        const { generatedResume, formData } = JSON.parse(str);
        if (!generatedResume) throw new Error("no resume data");
        await generateAndDownloadResume({
          ...generatedResume,
          template: formData?.template || "ats",
          font: formData?.font || "Calibri",
          sectionLayout: formData?.sectionLayout || "standard",
          onePage: formData?.onePage || false,
        });
      } else {
        // Tax, legal, and business documents regenerate from the same
        // pending form data the payment flow stored (keys match
        // PaymentSuccess). Types without extra args share one shape.
        const REDOWNLOAD = {
          "w2":                   { key: "pendingW2Data",                run: (d) => generateAndDownloadW2(d, localStorage.getItem("pendingW2TaxYear") || "2024") },
          "w9":                   { key: "pendingW9Data",                run: (d) => generateAndDownloadW9(d) },
          "1099-nec":             { key: "pending1099NECData",           run: (d) => generateAndDownload1099NEC(d, localStorage.getItem("pending1099NECTaxYear") || "2024") },
          "1099-misc":            { key: "pending1099MISCData",          run: (d) => generateAndDownload1099MISC(d, localStorage.getItem("pending1099MISCTaxYear") || "2024") },
          "schedule-c":           { key: "pendingScheduleCData",         run: (d) => generateAndDownloadScheduleC(d, localStorage.getItem("pendingScheduleCTaxYear") || "2024") },
          "bank-statement":       { key: "pendingBankStatementData",     run: (d) => generateAndDownloadBankStatement(d.formData || d, localStorage.getItem("pendingBankStatementTemplate") || "template-a") },
          "cease-and-desist":     { key: "pendingCeaseAndDesistData",    run: (d) => generateAndDownloadCeaseAndDesist(d) },
          "legal-document":       { key: "pendingLegalDocumentData",     run: (d) => generateAndDownloadLegalDocument(d) },
          "power-of-attorney":    { key: "pendingPowerOfAttorneyData",   run: (d) => generateAndDownloadPowerOfAttorney(d) },
          "commercial-lease":     { key: "pendingCommercialLeaseData",   run: (d) => generateAndDownloadCommercialLease(d) },
          "utility-bill":         { key: "pendingUtilityBillData",       run: (d) => generateAndDownloadUtilityBill(d, localStorage.getItem("pendingUtilityBillTemplate") || "electric") },
          "vehicle-bill-of-sale": { key: "pendingVehicleBillOfSaleData", run: (d) => generateAndDownloadVehicleBillOfSale(d) },
        };
        const entry = REDOWNLOAD[notif.type];
        if (!entry) throw new Error("unsupported type");
        const str = localStorage.getItem(entry.key);
        if (!str) throw new Error("no data");
        await entry.run(JSON.parse(str));
      }
    } catch {
      // a web guest has no My Documents to check, so it is not suggested to them
      setToastMessage(showMyDocs()
        ? "This file is no longer available. Check My Documents, or create a new document."
        : "This file is no longer available. You can create a new document anytime.");
      setToastOpen(true);
    } finally {
      setRedownloadingId(null);
    }
  };

  const segmentBtnStyle = {
    "--color":              "rgba(255,255,255,0.7)",
    "--color-checked":      "#ffffff",
    "--indicator-color":    "#ffffff",
    "--background-checked": "rgba(255,255,255,0.12)",
    "--border-radius":      "0",
    "--padding-top":        "0",
    "--padding-bottom":     "0",
    minHeight: 60,
    flexShrink: 0,
  };

  return (
    <IonApp className="admin-app">
      {/* Active discount banner — full width, above the sidebar and top bar.
          ion-app already lays out as a flex column; the split pane just needs
          to flow (relative + flex) instead of absolutely covering the banner. */}
      {/* discount codes are spent at checkout, which only the website has */}
      {!Capacitor.isNativePlatform?.() && <PromoBanner inApp />}
      <IonSplitPane
        contentId="app-main"
        when={(isLanding ? landingSidebarOpen : sidebarOpen && !isSecondaryPage) ? "md" : "(max-width: -1px)"}
        style={{ "--side-width": "300px", "--side-max-width": "300px", "--side-min-width": "300px", position: "relative", flex: "1 1 0%", width: "100%" }}
      >

        {/* ── Sidebar ── */}
        <IonMenu contentId="app-main" type="overlay" menuId="appSidebar" disabled={isMobile}>
          <IonHeader>
            <IonToolbar>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 12px", width: "100%" }}>
                {/* Close sidebar */}
                <IonButton fill="clear" className="app-icon-btn app-sidebar-icon-btn" onClick={handleCloseSidebar} aria-label="Close">
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px", color: "var(--ion-text-color)" }}>
                    <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>

                {/* Logo */}
                <button onClick={() => navigate("/app")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <img src={MintSlipLogo} alt="MintSlip" style={{ height: 30, width: "auto" }} />
                </button>

                {/* Dark mode toggle */}
                <IonButton fill="clear" className="app-icon-btn app-sidebar-icon-btn" onClick={toggleDark} title={darkMode ? "Light mode" : "Dark mode"}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px", color: "var(--ion-text-color)" }}>
                    <IonIcon icon={darkMode ? sunnyOutline : moonOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>
              </div>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            {/* Profile card — just the user (no business row in /app); tapping
                it opens Settings, or Sign in when there's no account yet */}
            <div style={{ paddingTop: 8 }}>
              <SidebarProfileCard rows={[...(business !== null ? [businessRow()] : []), ...(adminSide ? [mintslipRow()] : []), profileRow()]} />
            </div>

            {/* Nav links — directly under the profile card */}
            <div style={{ padding: "0 0 8px" }}>
              {topLinks.map(({ label, icon, src, path, href }) => (
                <button
                  key={label}
                  onClick={() => openLink({ path, href })} // the secondary page hides the rail itself; its open state stays for the way back
                  className="sidebar-nav-btn ion-activatable"
                  style={sidebarLinkStyle}
                >
                  <IonIcon icon={icon} src={src} style={{ fontSize: 24, flexShrink: 0, color: "inherit" }} />
                  {t(label)}
                  <IonRippleEffect />
                </button>
              ))}
            </div>

            {/* Bottom nav links — pinned via slot="fixed" */}
            <div slot="fixed" className="sidebar-bottom-group" style={{ bottom: 0, left: 0, right: 0, background: "var(--app-sidebar-bg)", borderTop: "1px solid var(--app-divider)", zIndex: 10 }}>
              {bottomLinks.map(({ label, icon, src, path }) => (
                <button
                  key={label}
                  onClick={() => navigate(path)}
                  className="sidebar-nav-btn ion-activatable"
                  style={sidebarLinkStyle}
                >
                  <IonIcon icon={icon} src={src} style={{ fontSize: 24, flexShrink: 0, color: "inherit" }} />
                  {t(label)}
                  <IonRippleEffect />
                </button>
              ))}
            </div>
          </IonContent>
        </IonMenu>

        {/* ── Main area ── */}
        <IonPage id="app-main">
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                {isSecondaryPage ? (
                  <IonButton fill="clear" onClick={() => navigate(-1)} style={{ "--color": "rgba(255,255,255,0.85)", "--border-radius": "50%" }}> {/* just go back; the rail keeps whatever state it had */}
                    <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px" }}>
                      <IonIcon icon={chevronBackOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                    </span>
                  </IonButton>
                ) : (
                  <IonButton fill="clear" onClick={handleMenuToggle} style={{ "--color": "rgba(255,255,255,0.85)", "--border-radius": "50%" }}>
                    <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px" }}>
                      <IonIcon icon={menuOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                    </span>
                  </IonButton>
                )}
              </IonButtons>

              {/* Middle: title/tabs that fade out when the search input scales
                  in over the same space (whodat topbar behavior). */}
              <div className="admin-topbar-mid" ref={search.midRef}>
                <div className={`admin-topbar-mid-content${search.open ? " is-hidden" : ""}`}>
              {isSecondaryPage ? (
                <IonTitle>{pageTitle}</IonTitle>
              ) : isMobile ? (
                <>
                  {/* Mobile: the current section as the whodat segment label
                      (uppercase, a caret at its end) opening the fresh-mounted
                      popover list, a line after each entry but the last; the
                      same design as the admin's top bar */}
                  <IonButton fill="clear" className="segment-button white" onClick={(e) => setNavMenu({ open: true, event: e.nativeEvent })}>
                    {t(visibleTabs.find(x => x.id === activeTab)?.label || "Home")}
                    <IonIcon slot="end" icon={caretDownOutline} />
                  </IonButton>
                  <FreshPopover open={navMenu.open} event={navMenu.event} onClose={() => setNavMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "240px" }}>
                    <IonContent>
                      <IonList style={{ padding: "4px 0" }}>
                        {visibleTabs.map((tab, i) => (
                          <IonItem
                            key={tab.id}
                            button
                            detail={false}
                            lines={i === visibleTabs.length - 1 ? "none" : "inset"}
                            aria-current={activeTab === tab.id ? "page" : undefined}
                            onClick={() => { setNavMenu({ open: false, event: undefined }); navigate(tab.path); }}
                            style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--color": "var(--ion-text-color)", "--border-color": "var(--app-divider)" }}
                          >
                            <IonIcon icon={tab.icon} slot="start" style={{ fontSize: 18 }} />
                            <IonLabel>{t(tab.label)}</IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </IonContent>
                  </FreshPopover>
                </>
              ) : (
                /* Desktop: scrollable segment */
                <IonSegment
                  scrollable
                  value={activeTab}
                  onIonChange={e => {
                    const tab = visibleTabs.find(t => t.id === e.detail.value);
                    if (tab) navigate(tab.path);
                  }}
                  style={{ "--background": "transparent", "--color": "rgba(255,255,255,0.65)", "--color-checked": "#ffffff", "--indicator-color": "#ffffff", flex: 1, minWidth: 0 }}
                >
                  {visibleTabs.map(tab => (
                    <IonSegmentButton key={tab.id} value={tab.id} layout="icon-start" style={segmentBtnStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px" }}>
                        <IonIcon icon={tab.icon} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                      </span>
                      <IonLabel style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {t(tab.label)}
                      </IonLabel>
                    </IonSegmentButton>
                  ))}
                </IonSegment>
              )}
                </div>
                {search.overlay}
              </div>

              {/* Search + bell — always in end slot */}
              <IonButtons slot="end">
                {search.button}
                {showNotifications() && (
                <IonButton fill="clear" className="app-icon-btn" onClick={openNotifDrawer} style={{ "--color": "rgba(255,255,255,0.85)", position: "relative" }}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px", position: "relative" }}>
                    <IonIcon icon={notificationsOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                    {unreadCount > 0 && (
                      <span style={{
                        position: "absolute", top: -6, right: -7,
                        background: "var(--ion-color-danger)", color: "var(--ion-color-danger-contrast)",
                        borderRadius: "50%", fontSize: 10, fontWeight: 700,
                        width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                        border: "2px solid #ffffff", boxSizing: "border-box",
                        lineHeight: 1, pointerEvents: "none",
                      }}>
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </span>
                </IonButton>
                )}
              </IonButtons>
            </IonToolbar>
            {/* The page's own segment row (Preferences: All / Branding; Billing:
                Subscription Plans / Wallet Balance): a second toolbar under the
                title, the admin's page-tabs pattern. It lives here in the main
                header: the sidebar menu is hidden on every secondary page. */}
            {pageTabs && pageTabs.items && pageTabs.items.length > 0 && (
              /* .app-page-tabs: the app's rows hold two or three tabs, so
                 they share the full width (the admin's hug their labels) */
              <IonToolbar className={`admin-page-tabs app-page-tabs${isMobile && pageTabs.dropdownOnMobile ? " toolbar-segment toolbar-label" : ""}`}>
                {isMobile && pageTabs.dropdownOnMobile ? (
                  <>
                    {/* On a phone a page that asks for it (Signatures,
                        Preferences) shows the current tab as the whodat segment
                        label opening a popover of the tabs, a line after each
                        but the last: the admin's page-tabs dropdown */}
                    <IonButton fill="clear" className="segment-button white" onClick={(e) => setPageTabMenu({ open: true, event: e.nativeEvent })}>
                      {(pageTabs.items.find((x) => x.value === pageTabs.value) || pageTabs.items[0]).label}
                      <IonIcon slot="end" icon={caretDownOutline} />
                    </IonButton>
                    <FreshPopover open={pageTabMenu.open} event={pageTabMenu.event} onClose={() => setPageTabMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "240px" }}>
                      <IonContent>
                        <IonList style={{ padding: "4px 0" }}>
                          {pageTabs.items.map((x, i) => (
                            <IonItem
                              key={x.value}
                              button
                              detail={false}
                              lines={i === pageTabs.items.length - 1 ? "none" : "inset"}
                              aria-current={pageTabs.value === x.value ? "page" : undefined}
                              onClick={() => { setPageTabMenu({ open: false, event: undefined }); changePageTab(x.value); }}
                              style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--color": "var(--ion-text-color)", "--border-color": "var(--app-divider)" }}
                            >
                              <IonLabel>{x.label}</IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      </IonContent>
                    </FreshPopover>
                  </>
                ) : (
                  <IonSegment
                    scrollable
                    value={pageTabs.value}
                    onIonChange={(e) => changePageTab(e.detail.value)}
                    style={{ "--background": "transparent", "--color": "rgba(255,255,255,0.65)", "--color-checked": "#ffffff", "--indicator-color": "#ffffff", flex: 1, minWidth: 0 }}
                  >
                    {pageTabs.items.map((t) => (
                      <IonSegmentButton key={t.value} value={t.value} layout="label-only" style={{ ...segmentBtnStyle, "--background-checked": "transparent", minHeight: 48 }}>
                        <IonLabel style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>{t.label}</IonLabel>
                      </IonSegmentButton>
                    ))}
                  </IonSegment>
                )}
              </IonToolbar>
            )}
          </IonHeader>

          {search.panel}

          <IonContent ref={mainContentRef} style={{ "--background": "var(--ion-background-color)" }}>
            {fillHeight ? (
              <div style={{ height: "100%", overflow: "hidden" }}>{children}</div>
            ) : (
              <div className="admin-page-content">{children}</div>
            )}
          </IonContent>

        </IonPage>

      </IonSplitPane>

      {/* ── IonToast for new downloads (fresh-mounted: see FreshOverlay) ── */}
      <FreshOverlay
        as={IonToast}
        buttons={TOAST_CLOSE_BUTTONS}
        isOpen={toastOpen}
        onDidDismiss={() => { setToastOpen(false); setToastColor("success"); }}
        message={toastMessage}
        duration={4000}
        position="top"
        color={toastColor}
        buttons={lastDownload && isMobileBrowser() ? [{
          // A tap (a real gesture) re-opens the file in a new tab if the
          // automatic one was blocked
          text: t("Download"),
          handler: () => { openDownloadInNewTab(lastDownload); return false; },
        }] : showNotifications() ? [{
          text: t("View"),
          handler: () => { openNotifDrawer(); return false; },
        }] : []}
      />

      {/* ── Floating Create button (portalled inside ion-app so IonActionSheet stacks above it) ── */}
      {!isSecondaryPage && createPortal(
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 200 }}>
          <IonButton
            onClick={() => setCreateOpen(true)}
            style={{ "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--background-hover": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)", "--box-shadow": "0 6px 20px rgba(0,0,0,0.25)" }}
          >
            <span slot="start" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem" }}>
              <IonIcon icon={addOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
            </span>
            {t("CREATE")}
          </IonButton>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      <FreshActionSheet
        isOpen={createOpen}
        mode="ios"
        onClose={() => setCreateOpen(false)}
        style={{ "--background": "var(--ion-card-background)", "--button-background": "var(--ion-card-background)", "--button-background-activated": "var(--ion-color-step-100)", "--button-color": "var(--ion-text-color)", "--backdrop-opacity": "0.5" }}
        buttons={(() => {
          // Each option carries its icon (the same ones the header tabs use)
          // and the id the business's Create-menu settings refer to. A
          // built-in the admin disabled, or the business hid, is left out;
          // the business's order comes first and anything it has not placed
          // keeps its default spot after.
          const builtIn = [
            { id: "paystub", on: !disabledGenerators.has("paystub"), text: t("Create Pay Stub"), icon: documentTextOutline, handler: () => navigate("/app/paystubs") },
            { id: "canadian-paystub", on: !disabledGenerators.has("canadian-paystub"), text: t("Create Canadian Paystub"), icon: leafOutline, handler: () => navigate("/app/canadian-paystub") },
            { id: "uk-payslip", on: !disabledGenerators.has("uk-payslip"), text: t("Create UK Payslip"), icon: receiptOutline, handler: () => navigate("/app/uk-payslips") },
            { id: "offer-letter", on: !disabledGenerators.has("offer-letter"), text: t("Create Offer Letter"), icon: mailOutline, handler: () => setOfferLetterOpen(true) },
            { id: "bank-statement", on: !disabledGenerators.has("bank-statement"), text: t("Create Accounting Mockup"), icon: walletOutline, handler: () => setAccountingOpen(true) },
            { id: "ai-resume", on: !disabledGenerators.has("ai-resume"), text: t("Build AI Resume"), icon: readerOutline, handler: () => setResumeBuilderOpen(true) },
            { id: "tax-forms", on: !groupAllDisabled(TAX_GENERATOR_IDS), text: t("Build Tax Forms"), icon: calculatorOutline, handler: () => navigate("/app/tax-forms") },
            { id: "legal-forms", on: !groupAllDisabled(LEGAL_GENERATOR_IDS), text: t("Build Legal Forms"), icon: scaleOutline, handler: () => navigate("/app/legal-forms") },
            { id: "business-forms", on: !groupAllDisabled(BUSINESS_GENERATOR_IDS), text: t("Build Business Forms"), icon: briefcaseOutline, handler: () => navigate("/app/business-forms") },
          ];
          const hidden = new Set(createMenu.hidden || []);
          const order = createMenu.order || [];
          const items = [
            ...builtIn.filter((b) => b.on && !hidden.has(b.id)),
            ...actionSheetCategories.map((c) => ({ id: c.slug, text: c.label, icon: documentTextOutline, handler: () => navigate(`/app/c/${c.slug}`) })),
          ];
          const rank = (it, i) => { const k = order.indexOf(it.id); return k === -1 ? order.length + i : k; };
          const sorted = items.map((it, i) => [it, rank(it, i)]).sort((a, b) => a[1] - b[1]).map(([it]) => ({ text: it.text, icon: it.icon, handler: it.handler }));
          return [...sorted, { text: t("Cancel"), icon: closeOutline, role: "cancel" }];
        })()}
      />

      {/* ── Notifications drawer — the shared whodat-style panel: list /
          select (edit icon) / preferences (sliders icon) ── */}
      {pmModalOpen && <PaymentMethodsModal onClose={() => setPmModalOpen(false)} />}
      {invModalOpen && <InvoicesModal onClose={() => setInvModalOpen(false)} />}
      <NotificationsDrawer
        open={notifDrawerOpen}
        onClose={closeNotifDrawer}
        zIndex={9994}
        items={visibleNotifications}
        onOpenItem={openPreview}
        onSetRead={(ids, read) => { setNotificationsRead(ids, read); refreshNotifications(); }}
        onDelete={(ids) => { removeNotifications(ids); refreshNotifications(); }}
        prefGroups={appNotifPrefGroups()}
        prefs={notifPrefs}
        onPrefChange={(key, value) => setNotifPrefs(setNotifPref("app", key, value))}
        renderItem={(notif) => (
          <>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--ion-text-color)", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.3 }}>
              {resolveNotifTitle(notif)}
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--ion-color-medium)", marginTop: 2 }}>
              {formatRelativeTime(notif.timestamp)}
            </div>
            {notif.status === "generating" && (() => { const o = notifStatuses.generating || {}; const col = notifStatusColor(o.color || "primary"); return (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                <IonSpinner name="crescent" style={{ width: 13, height: 13, color: col }} />
                <span style={{ fontSize: "0.72rem", color: col }}>{o.label || t("Generating...")}</span>
              </div>
            ); })()}
            {notif.status === "ready" && !isSignNotification(notif) && (() => { const o = notifStatuses.ready || {}; return (
              <div style={{ fontSize: "0.72rem", color: notifStatusColor(o.color || "success"), marginTop: 4, fontWeight: 500 }}>{o.label || t("Ready to download")}</div>
            ); })()}
            {notif.status === "error" && (() => { const o = notifStatuses.error || {}; return (
              <div style={{ fontSize: "0.72rem", color: notifStatusColor(o.color || "danger"), marginTop: 4 }}>{o.label || t("Generation failed")}</div>
            ); })()}
          </>
        )}
      />

      {/* ── Banned overlay — sits over the live app so its scrim blurs the
          real /app behind it, exactly like the paywall. Not dismissible. ── */}
      {isBanned && createPortal(
        <BannedScreen overlay />,
        document.querySelector("ion-app") || document.body
      )}

      {/* ── Notification preview modal — same portalled overlay style/size as
          the generator preview modals (e.g. the paystub preview): desktop is
          a centered 600px card on a dark backdrop, mobile is full-screen. ── */}
      {previewNotif && createPortal(
        <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10001, background: window.innerWidth >= 768 ? "rgba(0,0,0,0.5)" : "var(--ion-background-color, #f2f2f7)", display: "flex", alignItems: window.innerWidth >= 768 ? "center" : "stretch", justifyContent: window.innerWidth >= 768 ? "center" : "stretch" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: window.innerWidth >= 768 ? 600 : "100%", height: window.innerWidth >= 768 ? "auto" : "100%", maxHeight: window.innerWidth >= 768 ? "90vh" : "100%", overflow: "hidden" }}>
            <IonHeader>
              <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
                <IonButtons slot="start">
                  <IonButton fill="clear" shape="round" onClick={closePreview}>
                    <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                      <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                    </span>
                  </IonButton>
                </IonButtons>
                <IonTitle style={{ fontWeight: 700 }}>{resolveNotifTitle(previewNotif) || t("Preview")}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {previewNotif.status === "generating" || previewLoading ? (
                /* Same single full-size skeleton the sample cards use — one
                   flash in place at the document's aspect ratio until the
                   preview loads. */
                <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", borderRadius: 8 }}>
                  <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0 }} />
                </div>
              ) : previewNotif.status === "error" ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8, border: "2px dashed var(--ion-color-light-shade)" }}>
                  <IonIcon icon={notificationsOutline} style={{ fontSize: "2.5rem", color: "var(--ion-color-danger)", marginBottom: 8 }} />
                  <p style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", margin: 0 }}>{t("Generation failed")}</p>
                </div>
              ) : previewPages.length > 0 ? (
                <PreviewPager
                  pages={previewPages}
                  index={previewIndex}
                  onIndexChange={setPreviewIndex}
                  altPrefix={previewNotif.fileName || "Preview"}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8, border: "2px dashed var(--ion-color-light-shade)" }}>
                  <IonIcon icon={documentTextOutline} style={{ fontSize: "2.5rem", color: "var(--ion-color-medium)", marginBottom: 8 }} />
                  <p style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", margin: 0, lineHeight: 1.5 }}>
                    {t("Preview is no longer available, but you can still download your file.")}
                  </p>
                </div>
              )}

              {/* Actions — stacked full-width Ionic buttons: Download, then Delete */}
              <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
                {previewNotif.status === "ready" && (
                  <IonButton
                    expand="block"
                    color="success"
                    disabled={redownloadingId === previewNotif.id}
                    onClick={() => handleRedownload(previewNotif)}
                  >
                    {redownloadingId === previewNotif.id && (
                      <IonSpinner name="crescent" slot="start" style={{ width: 14, height: 14, color: "var(--ion-color-success-contrast)" }} />
                    )}
                    {t("Download")}
                  </IonButton>
                )}
                <IonButton
                  expand="block"
                  color="danger"
                  onClick={async () => {
                    if (await confirmAlert({ header: t("Delete this notification?"), message: previewNotif.fileName || "" })) {
                      removeNotification(previewNotif.id);
                      setNotifications(getNotifications());
                      closePreview();
                    }
                  }}
                >
                  {t("Delete")}
                </IonButton>
              </div>
            </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      {/* ── Mobile sidebar overlay ── */}
      {createPortal(<>
        {mobileSidebarOpen && (
          <div
            className="mob-sidebar-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 9996, background: "rgba(0,0,0,0.5)" }}
          />
        )}
        <div
          className="mob-sidebar-panel"
          style={{
            position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 9997,
            width: 300, maxWidth: "85vw",
            background: "var(--app-sidebar-bg, #ffffff)",
            boxShadow: mobileSidebarOpen ? "4px 0 24px rgba(0,0,0,0.25)" : "none",
            display: "flex", flexDirection: "column",
            overflowY: "auto",
            visibility: mobileSidebarOpen ? "visible" : "hidden",
            transform: mobileSidebarOpen ? "translateX(0)" : "translateX(-100%)",
            transition: mobileSidebarOpen ? "transform 0.28s cubic-bezier(0.4,0,0.2,1)" : "transform 0.28s cubic-bezier(0.4,0,0.2,1), visibility 0s linear 0.28s",
          }}
        >
          {/* Header (notched phones: starts below the status bar). Same Ionic
              buttons and bottom shadow as the web sidebar's toolbar. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(4px + env(safe-area-inset-top, 0px)) 12px 4px", minHeight: "calc(60px + env(safe-area-inset-top, 0px))", flexShrink: 0, position: "relative", zIndex: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.14)" }}>
            <IonButton fill="clear" className="app-icon-btn app-sidebar-icon-btn" onClick={() => setMobileSidebarOpen(false)} aria-label="Close">
              <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px", color: "var(--ion-text-color)" }}>
                <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
              </span>
            </IonButton>
            <button onClick={() => { navigate("/app"); setMobileSidebarOpen(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              <img src={MintSlipLogo} alt="MintSlip" style={{ height: 30, width: "auto" }} />
            </button>
            <IonButton fill="clear" className="app-icon-btn app-sidebar-icon-btn" onClick={toggleDark} title={darkMode ? "Light mode" : "Dark mode"}>
              <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px", color: "var(--ion-text-color)" }}>
                <IonIcon icon={darkMode ? sunnyOutline : moonOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
              </span>
            </IonButton>
          </div>

          {/* Profile card — user only (no business row in /app) */}
          <div style={{ flexShrink: 0 }}>
            <SidebarProfileCard rows={[...(business !== null ? [businessRow()] : []), ...(adminSide ? [mintslipRow()] : []), profileRow()]} />
          </div>

          {/* Nav links — directly under the profile card */}
          <div style={{ flexShrink: 0, padding: "0 0 8px" }}>
            {topLinks.map(({ label, icon, src, path, href }) => (
              <button
                key={label}
                onClick={() => { openLink({ path, href }); setMobileSidebarOpen(false); }}
                className="sidebar-nav-btn ion-activatable"
                style={{
                  width: "100%", background: "none", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 16,
                  padding: "0 20px", minHeight: 48, textAlign: "left",
                  color: "var(--ion-text-color)",
                  fontFamily: "var(--ion-font-family)", fontSize: "0.9375rem",
                  position: "relative", overflow: "hidden",
                }}
              >
                <IonIcon icon={icon} src={src} style={{ fontSize: 24, flexShrink: 0, color: "inherit" }} />
                {t(label)}
                <IonRippleEffect />
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Bottom nav links — pinned */}
          <div className="sidebar-bottom-group" style={{ flexShrink: 0, borderTop: "1px solid var(--app-divider)" }}>
            {bottomLinks.map(({ label, icon, src, path }) => (
              <button
                key={label}
                onClick={() => { navigate(path); setMobileSidebarOpen(false); }}
                className="sidebar-nav-btn ion-activatable"
                style={{
                  width: "100%", background: "none", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 16,
                  padding: "0 20px", minHeight: 48, textAlign: "left",
                  color: "var(--ion-text-color)",
                  fontFamily: "var(--ion-font-family)", fontSize: "0.9375rem",
                  position: "relative", overflow: "hidden",
                }}
              >
                <IonIcon icon={icon} src={src} style={{ fontSize: 24, flexShrink: 0, color: "inherit" }} />
                {t(label)}
                <IonRippleEffect />
              </button>
            ))}
          </div>
        </div>
      {/* Portaled into ion-app (not body): ion-app is its own stacking context,
          so anything mounted on body sits above every Ionic overlay, and the
          user-card popover would open underneath this drawer. */}
      </>, document.querySelector("ion-app") || document.body)}

      {/* ── Offer Letter modal ── */}
      <AppOfferLetter isOpen={offerLetterOpen} onClose={() => setOfferLetterOpen(false)} />

      {/* ── Accounting Mockup modal (config-driven, same flow as the doc modals) ── */}
      {accountingOpen && (
        <AppTaxFormModal config={BUSINESS_FORM_CONFIGS["bank-statement"]} onClose={() => setAccountingOpen(false)} />
      )}

      {/* ── Resume Builder modal ── */}
      <AppResumeBuilder isOpen={resumeBuilderOpen} onClose={() => setResumeBuilderOpen(false)} />

      {/* ── Support chat widget ── */}
      {/* Support bubble in the true bottom-right corner (the old 60px offset
          was for a tab bar the app no longer has) */}
      <SupportChatWidget currentUser={signedIn ? userInfo : null} />

      {upsellOffer && (
        <UpsellPaywall offer={upsellOffer} boughtLabel={upsellOffer.boughtLabel} previewImage={upsellOffer.previewImage || null} onPick={pickUpsell} onDismiss={dismissUpsell} />
      )}

      {/* ── Business-card popover (shared by the desktop rail and the mobile drawer) ── */}
      <FreshPopover
        open={bizMenu.open}
        event={bizMenu.event}
        onClose={() => setBizMenu({ open: false, event: undefined })}
        side="bottom"
        alignment="start"
        reference={mobileSidebarOpen ? "event" : "trigger"}
        style={{ "--width": "240px", "--offset-y": "4px" }}
      >
        <IonContent>
          <IonList lines="inset" className="house-menu" style={{ padding: "4px 0" }}>
            {businesses.length > 1 && (
              <IonItem
                button
                detail={false}
                onClick={() => { const ev = bizMenu.event; setBizMenu({ open: false, event: undefined }); setTimeout(() => setBizSwitcher({ open: true, event: ev }), 220); }}
                style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
              >
                <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                  <IonIcon icon={swapHorizontalOutline} style={{ fontSize: 18 }} />
                </div>
                <IonLabel>{t("Switch businesses")}</IonLabel>
              </IonItem>
            )}
            <IonItem
              button
              detail={false}
              onClick={() => { setBizMenu({ open: false, event: undefined }); setMobileSidebarOpen(false); navigate("/app/business"); }}
              style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
            >
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                <IonIcon icon={briefcaseOutline} style={{ fontSize: 18 }} />
              </div>
              <IonLabel>{t("Manage business")}</IonLabel>
            </IonItem>
            <IonItem
              button
              detail={false}
              onClick={() => { setBizMenu({ open: false, event: undefined }); setMobileSidebarOpen(false); setBizSetupOpen("create"); }}
              style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
            >
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                <IonIcon icon={addOutline} style={{ fontSize: 18 }} />
              </div>
              <IonLabel>{t("Add a business")}</IonLabel>
            </IonItem>
          </IonList>
        </IonContent>
      </FreshPopover>

      {/* ── MintSlip card popover: the admin's links into the admin area ── */}
      <FreshPopover
        open={adminMenu.open}
        event={adminMenu.event}
        onClose={() => setAdminMenu({ open: false, event: undefined })}
        side="bottom"
        alignment="start"
        reference={mobileSidebarOpen ? "event" : "trigger"}
        style={{ "--width": "240px", "--offset-y": "4px" }}
      >
        <IonContent>
          <IonList lines="inset" className="house-menu" style={{ padding: "4px 0" }}>
            <IonItem
              button
              detail={false}
              onClick={() => { setAdminMenu({ open: false, event: undefined }); setMobileSidebarOpen(false); navigate("/admin/site-settings"); }}
              style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
            >
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                <IonIcon icon={settingsOutline} style={{ fontSize: 18 }} />
              </div>
              <IonLabel>{t("Site settings")}</IonLabel>
            </IonItem>
          </IonList>
        </IonContent>
      </FreshPopover>

      {/* ── The business switcher (shared with Billing's business card) ── */}
      <BusinessSwitcherPopover
        open={bizSwitcher.open}
        event={bizSwitcher.event}
        onClose={() => setBizSwitcher({ open: false, event: undefined })}
        reference={mobileSidebarOpen ? "event" : "trigger"}
      />
      {bizSetupOpen && (
        <BusinessSetupModal
          mode={bizSetupOpen === "create" ? "create" : "setup"}
          onClose={() => setBizSetupOpen(false)}
          onSaved={bizSetupOpen === "create"
            ? (b) => { if (b && b.id) navigate(`/app/billing?business=${encodeURIComponent(b.id)}`); }
            : undefined}
        />
      )}

      {/* ── User-card popover (shared by the desktop rail and the mobile drawer) ── */}
      <FreshPopover
        open={userMenu.open}
        event={userMenu.event}
        onClose={closeUserMenu}
        side="bottom"
        alignment="start"
        reference={mobileSidebarOpen ? "event" : "trigger"}
        style={{ "--width": "240px", "--offset-y": "4px" }}
      >
        <IonContent>
          <IonList lines="inset" className="house-menu" style={{ padding: "4px 0" }}>
            {signedIn ? (
              <>
                <IonItem
                  button
                  detail={false}
                  onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); navigate("/app/account"); }}
                  style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
                >
                  <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                    <IonIcon icon={personOutline} style={{ fontSize: 18 }} />
                  </div>
                  <IonLabel>{t("Manage profile")}</IonLabel>
                </IonItem>
                {adminSide && (
                  <IonItem
                    button
                    detail={false}
                    onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); navigate("/admin/dashboard"); }}
                    style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
                  >
                    <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                      <IonIcon icon={speedometerOutline} style={{ fontSize: 18 }} />
                    </div>
                    <IonLabel>{t("Admin dashboard")}</IonLabel>
                  </IonItem>
                )}
                {showBilling && (
                  <IonItem
                    button
                    detail={false}
                    onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); navigate("/app/billing"); }}
                    style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
                  >
                    <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                      <IonIcon icon={walletOutline} style={{ fontSize: 18 }} />
                    </div>
                    <IonLabel>{t("Billing")}</IonLabel>
                  </IonItem>
                )}
                {/* payment methods and invoices live on the website; the app sells nothing (store rules) */}
                {!Capacitor.isNativePlatform?.() && (<>
                  <IonItem
                    button
                    detail={false}
                    onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); setPmModalOpen(true); }}
                    style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
                  >
                    <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                      <IonIcon icon={cardOutline} style={{ fontSize: 18 }} />
                    </div>
                    <IonLabel>{t("Payment methods")}</IonLabel>
                  </IonItem>
                  <IonItem
                    button
                    detail={false}
                    onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); setInvModalOpen(true); }}
                    style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
                  >
                    <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                      <IonIcon icon={receiptOutline} style={{ fontSize: 18 }} />
                    </div>
                    <IonLabel>{t("Invoices")}</IonLabel>
                  </IonItem>
                </>)}
                <IonItem
                  button
                  detail={false}
                  onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); handleUserLogout(); }}
                  style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", "--color": "var(--ion-color-danger)", fontSize: "0.9rem" }}
                >
                  <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                    <IonIcon icon={logOutOutline} style={{ fontSize: 18, color: "var(--ion-color-danger)" }} />
                  </div>
                  <IonLabel>{t("Log out")}</IonLabel>
                </IonItem>
              </>
            ) : (
              <IonItem
                button
                detail={false}
                onClick={() => { closeUserMenu(); setMobileSidebarOpen(false); navigate("/login"); }}
                style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--border-color": "var(--app-divider, var(--ion-border-color))", fontSize: "0.9rem" }}
              >
                <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}>
                  <IonIcon icon={logInOutline} style={{ fontSize: 18 }} />
                </div>
                <IonLabel>{t("Log in")}</IonLabel>
              </IonItem>
            )}
          </IonList>
        </IonContent>
      </FreshPopover>

      {/* The force-update wall: the house modal (full-screen on mobile), with
          the new version's What's New and the one way out - the store update */}
      {forceUpdate && createPortal(
        <div className="modal-backdrop pm-modal app-force-update" style={{ position: "fixed", inset: 0, zIndex: 10050, background: isMobile ? "var(--ion-background-color)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "center" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 560, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "85vh", overflow: "hidden" }}>
            <IonHeader>
              <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
                <IonTitle style={{ fontWeight: 700 }}>{t("Update required")}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <div className="pm-body" style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              <IonCard className="pm-card sheet-card" style={{ margin: 0 }}>
                <div className="sheet-card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <IonIcon icon={arrowUpCircleOutline} style={{ fontSize: 22, color: "var(--ion-color-success)", flexShrink: 0 }} />
                  {updateMeta.latest ? `${t("Version")} ${updateMeta.latest}` : t("A new version")}
                </div>
                <div style={{ padding: "0 16px 16px" }}>
                  <p style={{ margin: "0 0 12px", fontSize: "0.92rem", lineHeight: 1.55, color: "var(--ion-color-medium)" }}>
                    {t("This version of the MintSlip app is no longer supported. Update now to keep everything working.")}
                  </p>
                  {updateMeta.whatsNew.length > 0 && (
                    <>
                      <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--ion-text-color)", margin: "0 0 8px" }}>{t("What's new")}</div>
                      <ul style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 6, listStyleType: "disc" }}>
                        {updateMeta.whatsNew.map((item, i) => (
                          <li key={i} style={{ fontSize: "0.875rem", lineHeight: 1.5, color: "var(--ion-text-color)" }}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </IonCard>
              <IonButton expand="block" color="success" style={{ "--color": "#ffffff", marginTop: 14 }}
                onClick={() => { const u = (loveLinks || {})[devicePlatform()]; if (u) window.open(u, "_blank", "noopener"); }}>
                {t("Update now")}
              </IonButton>
            </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body
      )}
    </IonApp>
  );
}
